import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Modal,
  Paper,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
  styled,
  InputAdornment,
} from '@material-ui/core';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import CheckIcon from '@material-ui/icons/Check';
import { useSnackbar } from 'notistack';
import { EMAIL_REG_EXP } from '../../../shared/constants';
import { post } from '../../../shared/api/apiService';
import { useApiEssentials } from '../../../shared/hooks/useApiEssentials';
import { useApi, configApiRef } from '@backstage/core-plugin-api';

const DEFAULT_LINK_EXPIRATION_TIME = 'PT24H';

interface InviteFormData {
  application: string;
  email: string;
}

interface InviteLinkResponse {
  link: string;
}

const inviteSchema = yup.object({
  application: yup.string().required('This field is required'),
  email: yup
    .string()
    .required('This field is required')
    .matches(EMAIL_REG_EXP, 'Invalid email format'),
});

export interface InviteApplication {
  label: string;
  value: string;
}

export interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  applications: InviteApplication[];
}

const StyledPaper = styled(Paper)({
  width: 560,
  position: 'absolute',
  margin: 'auto',
  padding: '24px',
  borderRadius: 6,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  boxSizing: 'border-box',
  boxShadow: '0px 12px 16px 0px rgba(0, 0, 0, 0.58)',
});

const StyledTitle = styled(Typography)({
  fontSize: 20,
  letterSpacing: 0.15,
  fontFamily: 'Red Hat Display, sans-serif',
  color: '#092241',
  margin: 0,
});

export const InviteUserModal = ({
  isOpen,
  onClose,
  applications,
}: InviteUserModalProps) => {
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const { apiUrl, getIdToken } = useApiEssentials('/identity/api/v1');
  const { enqueueSnackbar } = useSnackbar();
  const configApi = useApi(configApiRef);
  const inviteLinkExpirationTime =
    configApi.getOptionalString('userManagement.inviteLinkExpirationTime') ||
    DEFAULT_LINK_EXPIRATION_TIME;

  const { control, handleSubmit, reset } = useForm<InviteFormData>({
    resolver: yupResolver(inviteSchema),
    defaultValues: { application: '', email: '' },
  });

  const handleClose = () => {
    reset();
    setInviteLink(null);
    setApiError(null);
    setCopied(false);
    onClose();
  };

  const handleInviteAnother = () => {
    reset();
    setInviteLink(null);
    setApiError(null);
    setCopied(false);
  };

  const onSubmit = async (data: InviteFormData) => {
    setIsPending(true);
    setApiError(null);
    try {
      const idToken = await getIdToken();
      const result = await post<InviteLinkResponse>(
        'password-reset/link',
        {
          clientId: data.application,
          email: data.email,
          linkTtl: inviteLinkExpirationTime,
        },
        apiUrl,
        idToken,
      );
      setInviteLink(result.link);
    } catch {
      setApiError('Something went wrong. Please try again.');
    } finally {
      setIsPending(false);
    }
  };

  const handleCopy = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink);
      enqueueSnackbar('Link copied to clipboard', { variant: 'success' });
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Modal
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1301,
      }}
      open={isOpen}
      onClose={handleClose}
    >
      <StyledPaper elevation={0}>
        <StyledTitle variant="h6">Generate Magic Link</StyledTitle>

        {!inviteLink && (
          <Typography variant="body1" color="textSecondary">
            Select an application and enter the user's email to generate a
            personalised magic link for direct access.
          </Typography>
        )}
        {inviteLink ? (
          <TextField
            variant="outlined"
            value={inviteLink}
            disabled
            fullWidth
            label="Invite Link"
            InputLabelProps={{ shrink: true }}
            helperText="This link expires in 24 hours. Share it directly with the user."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleCopy}
                    title="Copy to clipboard"
                    edge="end"
                  >
                    {copied ? <CheckIcon /> : <FileCopyOutlinedIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        ) : (
          <Box display="flex" flexDirection="column" style={{ gap: 24 }}>
            <Controller
              name="application"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  select
                  name={field.name}
                  value={field.value}
                  onChange={e => field.onChange(e.target.value)}
                  onBlur={field.onBlur}
                  label="Application"
                  variant="outlined"
                  required
                  InputLabelProps={{ shrink: true }}
                  error={!!error}
                  helperText={error?.message}
                  SelectProps={{ MenuProps: { disablePortal: true } }}
                >
                  {applications.map(p => (
                    <MenuItem key={p.value} value={p.value}>
                      {p.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="email"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  name={field.name}
                  value={field.value}
                  onChange={e => {
                    setApiError(null);
                    field.onChange(e);
                  }}
                  onBlur={field.onBlur}
                  variant="outlined"
                  label="Email"
                  type="email"
                  required
                  InputLabelProps={{ shrink: true }}
                  error={!!error || !!apiError}
                  helperText={error?.message ?? apiError}
                />
              )}
            />
          </Box>
        )}

        <Box display="flex" justifyContent="flex-end" style={{ gap: 16 }}>
          <Button onClick={handleClose} color="primary" variant="text">
            dismiss
          </Button>
          {inviteLink ? (
            <Button
              onClick={handleInviteAnother}
              variant="contained"
              color="primary"
            >
              Generate another
            </Button>
          ) : (
            <Button
              onClick={handleSubmit(onSubmit)}
              variant="contained"
              color="primary"
              disabled={isPending}
            >
              Generate
            </Button>
          )}
        </Box>
      </StyledPaper>
    </Modal>
  );
};
